// 1. React imports
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

// 2. External library imports
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { QueryClientProvider } from '@tanstack/react-query'
import { SnackbarProvider } from 'notistack'

// 3. Internal imports
import './init'
import { dayjsConfig } from './dayjsConfig'
import { queryClient } from './queryClient'
import { AppTheme } from './theme'
import { AuthProvider } from './context/AuthContext'
import App from './App'
import './config/amplify'
import { env } from './utils/env'
import { DevTools } from './devtools/DevTools'

// Initialize dayjs plugins
dayjsConfig.executeDayJsPlugins()

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('root element not found')

// Create the app content
const appContent = (
  <StrictMode>
    <ThemeProvider theme={AppTheme}>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        autoHideDuration={4000}
      >
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <BrowserRouter>
              <AuthProvider>
                <App />
              </AuthProvider>
            </BrowserRouter>
          </LocalizationProvider>
        </QueryClientProvider>
      </SnackbarProvider>
    </ThemeProvider>
  </StrictMode>
)

// Render with DevTools if enabled, otherwise render App directly and unregister service worker
if (env.VITE_ENABLE_DEV_TOOLS === 'TRUE') {
  createRoot(rootElement).render(<DevTools appSlot={appContent} />)
} else {
  // CRITICAL: Aggressively unregister service workers before rendering
  // Service workers persist across sessions and must be explicitly removed
  if ('serviceWorker' in navigator) {
    // Check if we've already attempted cleanup in this session
    // Wrap in try-catch as sessionStorage can throw in privacy/embedded contexts
    let hasAttemptedCleanup = false
    try {
      hasAttemptedCleanup = sessionStorage.getItem('sw-cleanup-attempted') === 'true'
    } catch (error) {
      // If sessionStorage is unavailable, treat as not attempted (allow cleanup)
      if (import.meta.env.DEV) {
        console.warn('sessionStorage unavailable, proceeding with cleanup check:', error)
      }
    }

    navigator.serviceWorker
      .getRegistrations()
      .then(async (registrations) => {
        if (registrations.length > 0 && !hasAttemptedCleanup) {
          if (import.meta.env.DEV) {
            console.warn(`🧹 Unregistering ${registrations.length} service worker(s)...`)
          }

          // Unregister all service workers and track which ones succeeded
          const unregisterResults = await Promise.all(
            registrations.map(async (registration) => {
              if (import.meta.env.DEV) {
                console.warn('  → Unregistering:', registration.scope)
              }
              const success = await registration.unregister()
              return { scope: registration.scope, success }
            })
          )

          // Check if at least one unregister succeeded
          const anySucceeded = unregisterResults.some((result) => result.success)

          if (!anySucceeded) {
            console.error('All service worker unregister attempts failed')
            // Still render the app (don't block), but don't mark as attempted
            createRoot(rootElement).render(appContent)
            return
          }

          if (import.meta.env.DEV) {
            const successCount = unregisterResults.filter((r) => r.success).length
            console.warn(`Successfully unregistered ${successCount}/${registrations.length} service worker(s)`)
          }

          // Mark cleanup as attempted only if at least one succeeded
          try {
            sessionStorage.setItem('sw-cleanup-attempted', 'true')
          } catch (error) {
            // If sessionStorage fails, log but continue (cleanup still succeeded)
            if (import.meta.env.DEV) {
              console.warn('Failed to mark cleanup as attempted in sessionStorage:', error)
            }
          }

          // Clear only service-worker-managed caches (not all caches)
          if ('caches' in window) {
            try {
              const serviceWorkerCachePrefixes = ['workbox-', 'msw-', 'runtime-', 'precache-']
              const allCacheNames = await caches.keys()
              const serviceWorkerCacheNames = allCacheNames.filter((cacheName) =>
                serviceWorkerCachePrefixes.some((prefix) => cacheName.startsWith(prefix))
              )

              if (import.meta.env.DEV) {
                console.warn(
                  `Clearing ${serviceWorkerCacheNames.length}/${allCacheNames.length} service worker cache(s)...`
                )
              }

              if (serviceWorkerCacheNames.length > 0) {
                await Promise.all(serviceWorkerCacheNames.map((cacheName) => caches.delete(cacheName)))
              }
            } catch (error) {
              // Cache clearing is best-effort, don't block if it fails
              console.error('Failed to clear service worker caches:', error)
            }
          }

          if (import.meta.env.DEV) {
            console.warn('Service workers cleaned up, reloading...')
          }

          // Force a hard reload to clear service worker from memory
          window.location.reload()
          return // Don't render yet, wait for reload
        }

        // If no service workers found or already cleaned up, render the app
        createRoot(rootElement).render(appContent)
      })
      .catch((error) => {
        console.error('Failed to query/unregister service workers:', error)
        // Render anyway to avoid blocking the app
        createRoot(rootElement).render(appContent)
      })
  } else {
    // No service worker support, render normally
    createRoot(rootElement).render(appContent)
  }
}
